// src/app/customer-details.jsx
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MdCall, MdTextsms, MdEmail, MdAdd, MdWork, MdReceipt, MdDescription } from 'react-icons/md';
import API_BASE_URL from '../apiConfig.js';

// Web token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

export default function CustomerDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  // ✅ Hooks at top-level
  const [customer, setCustomer] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [userPermissions, setUserPermissions] = useState(null);

  // Check if user has quote write permissions
  const hasCustomerWritePermissions = () => {
    return userPermissions?.customer_perm === 'write';
  };

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const customerPerm = parsedPermissions?.customer_perm;

          // Block access if user is NOT (Owner OR Admin) AND has no invoice permission
          if ((user_type !== 'owner' && user_type !== 'admin') && customerPerm === 'none') {
            alert("You don't have permission to view customer information. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);
  // Permission check functions
  const canCreateJobs = () => {
    return userPermissions?.job_perm === 'write';
  };

  const canCreateInvoices = () => {
    return userPermissions?.invoice_perm === 'write';
  };

  const canCreateQuotes = () => {
    return userPermissions?.quote_perm === 'write';
  };

  const canViewJobs = () => {
    return userPermissions?.job_perm !== 'none';
  };

  const canViewInvoices = () => {
    return userPermissions?.invoice_perm !== 'none';
  };

  const canViewQuotes = () => {
    return userPermissions?.quote_perm !== 'none';
  };

  useEffect(() => {
    let cancelled = false;

    async function loadCustomer() {
      setLoading(true);
      setErr('');
      setCustomer(null);

      if (!id) {
        setErr('Missing customer id.');
        setLoading(false);
        return;
      }

      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      try {
        // Try the `/customers/id/:id` shape first (seen elsewhere in your app)
        const r1 = await axios.get(`${API_BASE_URL}/api/customers/id/${encodeURIComponent(id)}`, { headers });
        if (!cancelled) {
          setCustomer(r1.data);
          setForm(r1.data || {});
          setLoading(false);
          return;
        }
      } catch (e1) {
        // Fallback to `/customers/:id`
        try {
          const r2 = await axios.get(`${API_BASE_URL}/api/customers/${encodeURIComponent(id)}`, { headers });
          if (!cancelled) {
            setCustomer(r2.data);
            setForm(r2.data || {});
            setLoading(false);
            return;
          }
        } catch (e2) {
          if (!cancelled) {
            console.error('Failed to load customer with both endpoints:', e1, e2);
            setErr('Failed to load customer.');
            setLoading(false);
          }
        }
      }
    }

    loadCustomer();
    return () => { cancelled = true; };
  }, [id]);

  const saveChanges = async () => {
    if (!customer) return;
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      await axios.put(`${API_BASE_URL}/api/customers/${encodeURIComponent(customer.id)}`, form, { headers });

      // re-fetch to display fresh data
      const { data } = await axios.get(`${API_BASE_URL}/api/customers/${encodeURIComponent(customer.id)}`, { headers });
      setCustomer(data);
      setForm(data || {});
      setEditing(false);
    } catch (error) {
      console.error('Error saving customer changes:', error);
      alert('Failed to save changes.');
    }
  };

  const deleteCustomer = async () => {
    if (!customer) return;
    const ok = window.confirm(
      'Deleting this customer will permanently remove all invoices and jobs associated with them. Are you sure?'
    );
    if (!ok) return;

    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      await axios.delete(`${API_BASE_URL}/api/customers/${encodeURIComponent(customer.id)}`, { headers });
      navigate('/view-customers', { replace: true });
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Failed to delete customer.');
    }
  };

  const callCustomer = () => {
    if (customer?.phone) window.open(`tel:${customer.phone}`, '_self');
  };
  const textCustomer = () => {
    if (customer?.phone) window.open(`sms:${customer.phone}`, '_self');
  };
  const emailCustomer = () => {
    if (customer?.email) window.open(`mailto:${customer.email}`, '_self');
  };

  // ✅ Rendering guards AFTER hooks are declared
  if (!id) {
    return (
      <View style={styles.center}>
        <Text style={styles.label}>No customer id provided.</Text>
        <TouchableOpacity onPress={() => navigate(-1)} style={styles.goBackBtn}>
          <Text style={styles.goBackText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (loading) {
    return <View style={styles.center}><Text>Loading…</Text></View>;
  }

  if (err) {
    return (
      <View style={styles.center}>
        <Text>{err}</Text>
        <TouchableOpacity onPress={() => navigate(-1)} style={styles.goBackBtn}>
          <Text style={styles.goBackText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!customer) {
    return <View style={styles.center}><Text>No data.</Text></View>;
  }

  const fields = ['name', 'address', 'city', 'state', 'zip', 'phone', 'email', 'notes'];

  return (
    <KeyboardAvoidingView
      style={{ minHeight: '100vh', backgroundColor: '#fff' }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={100}
    >
      <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
                {fields.map((field) => (
          <View key={field} style={styles.fieldGroup}>
            <Text style={styles.label}>{field.charAt(0).toUpperCase() + field.slice(1)}:</Text>

            {editing ? (
              <TextInput
                style={styles.input}
                value={String(form[field] ?? '')}
                onChangeText={(text) => setForm((p) => ({ ...p, [field]: text }))}
                multiline={field === 'notes'}
              />
            ) : (
              <View style={styles.infoRow}>
                <Text style={styles.text}>
                  {field === 'notes' 
                    ? (customer[field] || 'No notes available')
                    : field === 'address'
                    ? (customer[field] || 'No Address')
                    : field === 'city'
                    ? (customer[field] || 'No City')
                    : field === 'state'
                    ? (customer[field] || 'No State')
                    : field === 'zip'
                    ? (customer[field] || 'No Zip')
                    : (customer[field] || '')
                  }
                </Text>

                {field === 'email' && customer.email ? (
                  <TouchableOpacity onPress={emailCustomer} style={styles.iconButtonGreen}>
                    <MdEmail size={20} color="white" />
                    <Text style={styles.contactButtonText}>Email</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            )}
          </View>
        ))}

        {editing ? (
          <View style={styles.buttonRow}>
            <TouchableOpacity style={[styles.saveButton, styles.editButton]} onPress={saveChanges}>
              <Text style={styles.buttonText}>Save</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.cancelButton, styles.deleteButton]}
              onPress={() => {
                setEditing(false);
                setForm(customer); // reset edits
              }}
            >
              <Text style={styles.buttonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View>
            {hasCustomerWritePermissions() && (
              <View style={styles.buttonRow}>
                <TouchableOpacity style={styles.editButton} onPress={() => setEditing(true)}>
                  <Text style={styles.buttonText}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.deleteButton} onPress={deleteCustomer}>
                  <Text style={styles.buttonText}>Delete</Text>
                </TouchableOpacity>
              </View>
            )}
        
            {/* Customer Action Panel */}
            <View style={styles.actionContainer}>
              <Text style={styles.customerActionLabel}>Customer Action</Text>
              <View style={styles.divider} />
        
              <View style={styles.buttonContainer}>
                <View style={styles.buttonRow}>
                  <TouchableOpacity
                    style={[styles.iconButton, !canCreateJobs() && { backgroundColor: '#ccc' }]}
                    onPress={() => canCreateJobs() && navigate(`/new-job?customerName=${encodeURIComponent(customer.name)}`)}
                    disabled={!canCreateJobs()}
                  >
                    <MdAdd size={24} color="#007AFF" />
                    <Text style={styles.bottomButtonText}>New Job</Text>
                  </TouchableOpacity>
        
                  <TouchableOpacity
                    style={[styles.iconButton, !canViewJobs() && { backgroundColor: '#ccc' }]}
                    onPress={() =>
                      canViewJobs() && navigate(
                        `/customer-jobs?customerName=${encodeURIComponent(customer.name)}&customerId=${encodeURIComponent(
                          customer.id
                        )}`
                      )
                    }
                    disabled={!canViewJobs()}
                  >
                    <MdWork size={24} color="#007AFF" />
                    <Text style={styles.bottomButtonText}>View Jobs</Text>
                  </TouchableOpacity>
                </View>
        
                <View style={styles.buttonRow}>
                  <TouchableOpacity
                    style={[styles.iconButton, !canCreateInvoices() && { backgroundColor: '#ccc' }]}
                    onPress={() => canCreateInvoices() && navigate(`/create-invoice?customerName=${encodeURIComponent(customer.name)}`)}
                    disabled={!canCreateInvoices()}
                  >
                    <MdAdd size={24} color="#007AFF" />
                    <Text style={styles.bottomButtonText}>New Invoice</Text>
                  </TouchableOpacity>
        
                  <TouchableOpacity
                    style={[styles.iconButton, !canViewInvoices() && { backgroundColor: '#ccc' }]}
                    onPress={() =>
                      canViewInvoices() && navigate(
                        `/customer-invoices?customerName=${encodeURIComponent(
                          customer.name
                        )}&customerId=${encodeURIComponent(customer.id)}`
                      )
                    }
                    disabled={!canViewInvoices()}
                  >
                    <MdReceipt size={24} color="#007AFF" />
                    <Text style={styles.bottomButtonText}>View Invoices</Text>
                  </TouchableOpacity>
                </View>
        
                <View style={styles.buttonRow}>
                  <TouchableOpacity
                    style={[styles.iconButton, !canCreateQuotes() && { backgroundColor: '#ccc' }]}
                    onPress={() => canCreateQuotes() && navigate(`/create-quote?customerName=${encodeURIComponent(customer.name)}`)}
                    disabled={!canCreateQuotes()}
                  >
                    <MdAdd size={24} color="#007AFF" />
                    <Text style={styles.bottomButtonText}>New Quote</Text>
                  </TouchableOpacity>
        
                  <TouchableOpacity
                    style={[styles.iconButton, !canViewQuotes() && { backgroundColor: '#ccc' }]}
                    onPress={() =>
                      canViewQuotes() && navigate(
                        `/customer-quotes?customerName=${encodeURIComponent(
                          customer.name
                        )}&customerId=${encodeURIComponent(customer.id)}`
                      )
                    }
                    disabled={!canViewQuotes()}
                  >
                    <MdDescription size={24} color="#007AFF" />
                    <Text style={styles.bottomButtonText}>View Quotes</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  center: { minHeight: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', padding: 20 },
  goBackBtn: { marginTop: 10, backgroundColor: '#007BFF', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 6 },
  goBackText: { color: '#fff', fontWeight: '600' },

  container: {
    padding: 20,
    paddingBottom: 100,
    backgroundColor: '#fff',
  },
  fieldGroup: { marginBottom: 15 },
  label: {
    fontWeight: 'bold',
    marginTop: 5,
    fontSize: 20,
    color: '#333',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 5,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 5,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  text: { fontSize: 16, color: '#555', flexShrink: 1, marginTop: 5 },

  infoRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  iconGroup: { flexDirection: 'row' },

  iconButton: {
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    flex: 1,
  },
  iconButtonGreen: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'green',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 5,
    marginLeft: 10,
  },

  buttonRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 20, gap: 30 },
  editButton: { backgroundColor: 'blue', padding: 10, borderRadius: 5, flex: 1, alignItems: 'center' },
  deleteButton: { backgroundColor: 'red', padding: 10, borderRadius: 5, flex: 1, alignItems: 'center' },
  saveButton: { backgroundColor: 'blue', padding: 12, borderRadius: 5, alignItems: 'center' },
  cancelButton: { backgroundColor: 'red', padding: 12, borderRadius: 5, alignItems: 'center' },
  buttonText: { color: 'white', fontWeight: 'bold', textAlign: 'center' },
  cancelButtonText: { color: 'white', fontWeight: 'bold', textAlign: 'center' },

  bottomButton: { alignItems: 'center', padding: 10, borderRadius: 5, flex: 1, marginHorizontal: 5 },
  bottomButtonText: { color: 'black', fontWeight: 'bold', textAlign: 'center' },

  customerActionLabel: { fontWeight: 'bold', marginTop: 10, marginBottom: 5, fontSize: 18, textAlign: 'center' },
  actionContainer: {
    backgroundColor: '#f9f9f9',
    borderRadius: 10,
    padding: 15,
    marginTop: 20,
    marginBottom: 20,
    elevation: 1,
  },
  divider: { height: 1, backgroundColor: '#ccc' },
  contactButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 5,
  }
});
